import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSeo } from '../hooks/useSeo.ts';

/**
 * Public status page. Polls /api/health/live and /api/health/ready on a 30s loop, plots a
 * rolling 60-minute uptime strip, and shows component-level breakdown. Lightweight enough to
 * skip a third-party (Statuspage / BetterStack) — wire those in later if you grow past the
 * point where a Render-side ping is enough.
 *
 * B2B procurement teams check this page before signing contracts. Even a barebones version
 * communicates "this is a real ongoing operation, not a side project that might disappear."
 */

type CheckResult = 'ok' | 'down' | 'pending';

type CheckState = {
  name: string;
  url: string;
  result: CheckResult;
  latencyMs: number | null;
  /** Rolling history of the last N polls. Oldest first. */
  history: CheckResult[];
};

const HISTORY_LENGTH = 60; // 60 polls × 30s = 30 minutes of recent history shown as bars
const POLL_INTERVAL_MS = 30_000;

const INITIAL_CHECKS: CheckState[] = [
  { name: 'API server', url: '/api/health/live', result: 'pending', latencyMs: null, history: [] },
  { name: 'Database', url: '/api/health/ready', result: 'pending', latencyMs: null, history: [] },
];

async function ping(url: string): Promise<{ result: CheckResult; latencyMs: number }> {
  const start = performance.now();
  try {
    const res = await fetch(url, { method: 'GET', credentials: 'omit' });
    const latencyMs = Math.round(performance.now() - start);
    return { result: res.ok ? 'ok' : 'down', latencyMs };
  } catch {
    const latencyMs = Math.round(performance.now() - start);
    return { result: 'down', latencyMs };
  }
}

export default function StatusPage() {
  useSeo({
    title: 'System status',
    canonicalPath: '/status',
    description:
      'Live operational status for EAA Proof — API, database, and historical uptime. Updated every 30 seconds.',
  });

  const [checks, setChecks] = useState<CheckState[]>(INITIAL_CHECKS);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function runChecks() {
      const next = await Promise.all(
        checks.map(async (c) => {
          const r = await ping(c.url);
          return {
            ...c,
            result: r.result,
            latencyMs: r.latencyMs,
            history: [...c.history.slice(-(HISTORY_LENGTH - 1)), r.result],
          };
        })
      );
      if (!cancelled) {
        setChecks(next);
        setLastUpdated(new Date());
      }
    }

    runChecks();
    const id = setInterval(runChecks, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
    // We deliberately depend only on the immutable URL list — recreating the timer every
    // poll would defeat the polling cadence.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const overallResult: CheckResult = useMemo(() => {
    if (checks.some((c) => c.result === 'down')) return 'down';
    if (checks.every((c) => c.result === 'ok')) return 'ok';
    return 'pending';
  }, [checks]);

  return (
    <div className="page status-page">
      <header className="status-header">
        <h1>System status</h1>
        <p className="muted">
          Live health of the EAA Proof platform — refreshes every 30 seconds in your browser.
          For incident notifications follow{' '}
          {/* TODO: USER INPUT — replace placeholder with your real notification channel
              (Twitter, email list, RSS, BetterStack public status page). */}
          <a href="mailto:support@eaaproof.com">support@eaaproof.com</a>.
        </p>
      </header>

      <section className={`status-overall status-${overallResult}`}>
        <span className="status-dot" aria-hidden="true" />
        <div>
          <h2>
            {overallResult === 'ok' && 'All systems operational'}
            {overallResult === 'down' && 'Service disruption detected'}
            {overallResult === 'pending' && 'Checking…'}
          </h2>
          {lastUpdated && (
            <p className="muted small">
              Last updated{' '}
              <time dateTime={lastUpdated.toISOString()}>
                {lastUpdated.toLocaleTimeString()}
              </time>
            </p>
          )}
        </div>
      </section>

      <section className="status-list">
        {checks.map((c) => (
          <article key={c.name} className={`status-row status-row-${c.result}`}>
            <div className="status-row-head">
              <div>
                <h3>{c.name}</h3>
                <p className="muted small">{c.url}</p>
              </div>
              <div className="status-row-result">
                <span className={`status-pill status-pill-${c.result}`}>
                  {c.result === 'ok' && 'Operational'}
                  {c.result === 'down' && 'Down'}
                  {c.result === 'pending' && 'Checking…'}
                </span>
                {c.latencyMs !== null && c.result === 'ok' && (
                  <span className="muted small status-latency">{c.latencyMs} ms</span>
                )}
              </div>
            </div>
            <div className="status-history" aria-label={`Last ${c.history.length} polls`}>
              {Array.from({ length: HISTORY_LENGTH }).map((_, i) => {
                const offset = HISTORY_LENGTH - c.history.length;
                const v = i < offset ? null : c.history[i - offset];
                return (
                  <span
                    key={i}
                    className={
                      v === 'ok'
                        ? 'status-tick status-tick-ok'
                        : v === 'down'
                        ? 'status-tick status-tick-down'
                        : 'status-tick status-tick-empty'
                    }
                  />
                );
              })}
            </div>
          </article>
        ))}
      </section>

      <section className="status-footer">
        <p className="muted small">
          Looking for incident history?{' '}
          {/* TODO: USER INPUT — wire to BetterStack / Statuspage when you graduate past
              a self-hosted polling page. Until then, post incident postmortems on the
              changelog. */}
          See the <Link to="/changelog">changelog</Link> for past disruptions. SLA targets
          are documented in the <Link to="/terms">terms of service</Link>.
        </p>
      </section>
    </div>
  );
}
